/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component } from 'react'
import { omit } from 'lodash'

import { EuiFlexGroup, EuiFlexItem, EuiLoadingSpinner, EuiPanel, EuiSpacer } from '@elastic/eui'

import type { CreateDeploymentTemplateV2UrlParams } from '@modules/cloud-api/v1/urls'
import {
  createDeploymentTemplateV2Url,
  setDeploymentTemplateV2Url,
} from '@modules/cloud-api/v1/urls'
import { withErrorBoundary } from '@modules/cui/Boundary'
import { createCuiWizard } from '@modules/cui/Wizard'
import type {
  DeploymentTemplateInfoV2,
  DeploymentTemplateRequestBody,
} from '@modules/cloud-api/v1/types'
import type { NonEmptyArray } from '@modules/ts-utils'

import ApiRequestExample from '../../../ApiRequestExample'
import DeploymentTemplateArchitectureViz from '../DeploymentTemplateArchitectureViz'

import { stackFeaturesStepDefinition } from './ConfigureTemplateStackFeaturesStep'
import { indexManagementStepDefinition } from './ConfigureTemplateIndexManagementStep'
import { instancesStepDefinition } from './ConfigureTemplateInstancesStep'
import { StepOwnProps } from './deploymentTemplateWizardTypes'

import type { StepDefinition } from './deploymentTemplateWizardTypes'
import type { AllProps as Props } from './types'

import './deploymentTemplateWizard.scss'

type TemplateInfo = DeploymentTemplateRequestBody | DeploymentTemplateInfoV2

const steps: NonEmptyArray<StepDefinition> = [
  instancesStepDefinition,
  indexManagementStepDefinition,
  stackFeaturesStepDefinition,
]

const WizardSteps = createCuiWizard<StepOwnProps>()

class DeploymentTemplateWizard extends Component<Props> {
  componentDidMount() {
    this.props.fetchSnapshotRepositories()
  }

  render() {
    const { isLoading, instanceTypes, instanceConfigurations, prompt } = this.props

    if (isLoading || instanceConfigurations == null || instanceTypes == null) {
      return <EuiLoadingSpinner data-test-id='wizard-loading' />
    }

    return (
      <div>
        {prompt}
        {prompt && <EuiSpacer size='xl' />}
        {this.renderWizard()}
      </div>
    )
  }

  renderWizard() {
    const {
      error,
      isSaving,
      saveButtonLabel,
      regionId,
      template,
      instanceConfigurations,
      updateDeploymentTemplate,
    } = this.props

    const initialStepDefinition = this.getInitialStepDefinition()

    const stepProps = {
      instanceConfigurations: instanceConfigurations!, // not null if we reach this method
      template,
      updateDeploymentTemplate,
      templateICs: (template as DeploymentTemplateInfoV2).instance_configurations,
      regionId,
    }

    return (
      <EuiFlexGroup>
        <EuiFlexItem className='deploymentTemplateWizard'>
          <EuiPanel paddingSize='l'>
            <WizardSteps
              steps={steps}
              stepProps={stepProps}
              completeButtonLabel={saveButtonLabel}
              completeButtonHelpComponent={
                <ApiRequestExample
                  method={this.getAPIMethod(template)}
                  endpoint={this.getDeploymentTemplateV2Url(template, { region: regionId })}
                  body={this.toDeploymentTemplateRequestBody(template)}
                />
              }
              onComplete={this.props.onSave}
              isBusyCompleting={isSaving}
              completionError={error}
              initialStep={initialStepDefinition}
            />
          </EuiPanel>
        </EuiFlexItem>
        {this.renderArchitecture()}
      </EuiFlexGroup>
    )
  }

  renderArchitecture() {
    return (
      <DeploymentTemplateArchitectureViz
        template={this.props.template}
        instanceConfigurations={this.props.instanceConfigurations}
        render={(className, content) => (
          <EuiFlexItem grow={false} className={className}>
            {content}
          </EuiFlexItem>
        )}
      />
    )
  }

  getAPIMethod(templateBodyOrInfo: TemplateInfo): 'POST' | 'PUT' {
    return this.isExistingDeploymentTemplate(templateBodyOrInfo) ? 'PUT' : 'POST'
  }

  isExistingDeploymentTemplate(
    templateBodyOrInfo: TemplateInfo,
  ): templateBodyOrInfo is DeploymentTemplateInfoV2 {
    return 'id' in templateBodyOrInfo
  }

  getDeploymentTemplateV2Url(
    templateInfo: TemplateInfo,
    params: CreateDeploymentTemplateV2UrlParams,
  ): string {
    return this.isExistingDeploymentTemplate(templateInfo)
      ? setDeploymentTemplateV2Url({
          ...params,
          templateId: templateInfo.id,
        })
      : createDeploymentTemplateV2Url(params)
  }

  toDeploymentTemplateRequestBody(templateBodyOrInfo: TemplateInfo): DeploymentTemplateRequestBody {
    if (this.isExistingDeploymentTemplate(templateBodyOrInfo)) {
      return omit(templateBodyOrInfo, ['id', 'source', 'instance_configurations'])
    }

    return templateBodyOrInfo
  }

  getInitialStepDefinition() {
    const { initialStep } = this.props

    switch (initialStep) {
      case `indexManagement`:
        return indexManagementStepDefinition
      default:
        return
    }
  }
}

export default withErrorBoundary(DeploymentTemplateWizard)
